import React, { useCallback, useRef, useState } from 'react'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import axiosInstance from '../../api/axiosInstance'
import Departamentos from '../../../src/assets/data/departamentos.json'
import Ciudades from '../../../src/assets/data/ciudades.json'
import { tiposDeDocumento, formaPago, metodoPago, plazosPago } from '../../assets/data/arrays'
import OrderForm from '../forms/OrderForm'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import BoldCreateOrderModal, { type BoldModalPhase } from '../modals/BoldCreateOrderModal'
import { copyToClipboard } from '../../utils/clipboard'
import { validateTransactionIdsForCreate } from '../../utils/validateTransactionIdsBeforeOrderSave'
import { deleteOrderById } from '../../services/orders/deleteOrderService'
import {
  generateAndSaveSingleBoldLinkForOrder,
  retrySaveBoldTransactionOnOrder,
  type BoldPatchRetryContext
} from '../../services/payments/boldSingleOrderLinkFlow'
import { parseClienteNetoFlag } from '../../utils/clienteNetoFlag'
import { getDefaultOrderStatusByPaymentMethod } from '../../utils/orderDefaultStatusByPayment'
import {
  extractAxiosApiErrorMessage,
  FlexyStockInsufficientOrderError,
  getFlexyStockInsufficientFromError,
  parseFlexyStockInsufficientCreatedOrder,
  buildFlexyStockInsufficientToastMessage,
  isFlexyStockInsufficientError
} from '../../utils/apiErrorMessage'
import {
  generateAndPatchBoldLinksForPartialRows,
  getPagosFromSubmitPayload,
  indicesPagosParcialesBoldSinTx,
  retrySaveBoldPartialLinksOnOrder,
  submitPayloadNeedsPartialBoldLinks,
  type BoldPartialLinkItem,
  type BoldPartialPatchContext
} from '../../services/payments/boldPartialLinksAfterOrderSave'
import { BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED } from '../../services/payments/boldPatchRetry'

function normalizeCotizacionToOrder(cotizacion: any) {
  if (!cotizacion) return {}
  const meta = (key: string) => {
    const found = Array.isArray(cotizacion.meta_data)
      ? cotizacion.meta_data.find((m: any) => m.key === key)
      : null
    return found ? found.value : ''
  }
  const customer = {
    first_name: cotizacion.customer?.first_name || cotizacion.billingAddress?.first_name || '',
    last_name: cotizacion.customer?.last_name || cotizacion.billingAddress?.last_name || '',
    email: cotizacion.customer?.email || '',
    tipo_documento: cotizacion.customer?.tipo_documento || meta('tipo_documento') || '',
    document:
      cotizacion.customer?.document ||
      cotizacion.customer?.documento ||
      meta('documento') ||
      meta('document') ||
      ''
  }
  const phone =
    cotizacion.billingAddress?.phone ||
    cotizacion.shippingAddress?.phone ||
    meta('phone') ||
    cotizacion.phone ||
    ''
  const billingAddress = {
    address_1: cotizacion.billingAddress?.address_1 || cotizacion.billing?.address_1 || '',
    city: cotizacion.billingAddress?.city || cotizacion.billing?.city || '',
    state: cotizacion.billingAddress?.state || cotizacion.billing?.state || '',
    phone,
    first_name: cotizacion.billingAddress?.first_name || cotizacion.customer?.first_name || '',
    last_name: cotizacion.billingAddress?.last_name || cotizacion.customer?.last_name || ''
  }
  const shippingAddress = {
    address_1: cotizacion.shippingAddress?.address_1 || cotizacion.shipping?.address_1 || '',
    city: cotizacion.shippingAddress?.city || cotizacion.shipping?.city || '',
    state: cotizacion.shippingAddress?.state || cotizacion.shipping?.state || '',
    phone: cotizacion.shippingAddress?.phone || phone
  }
  const lineItems = cotizacion.line_items || cotizacion.lineItems || []
  const extraDetails = {
    ...cotizacion.extraDetails,
    cliente_neto: parseClienteNetoFlag(
      cotizacion.extraDetails?.cliente_neto ?? meta('cliente_neto')
    ),
    destino_pedido:
      cotizacion.extraDetails?.destino_cotizacion || cotizacion.extraDetails?.destino_pedido || '',
    forma_pago: meta('forma_pago') || cotizacion.extraDetails?.forma_pago || '',
    payment_method: cotizacion.payment_method || meta('payment_method') || '',
    customer_note: cotizacion.extraDetails?.customer_note || '',
    expiration_limit: meta('expiration_limit') || '',
    costo_envio: cotizacion.extraDetails?.costo_envio || cotizacion.shipping_total || ''
  }
  const agente =
    typeof cotizacion.agente === 'object' && cotizacion.agente
      ? cotizacion.agente.id
      : cotizacion.agente || ''
  let creatorRoleId: number | undefined
  try {
    creatorRoleId = JSON.parse(localStorage.getItem('user') || '{}').role_id
  } catch {
    creatorRoleId = undefined
  }
  return {
    status: getDefaultOrderStatusByPaymentMethod(extraDetails.payment_method, creatorRoleId),
    payment_method: cotizacion.payment_method || meta('payment_method') || '',
    store_name: cotizacion.store_name || '',
    agente,
    guia: cotizacion.guia || '',
    total: cotizacion.total || '',
    customer,
    billingAddress,
    shippingAddress,
    extraDetails,
    lineItems,
    cotizacion_id:
      meta('cotizacion_id') || cotizacion.id || cotizacion.cotizaciones_id || ''
  }
}

const BOLD_PROGRESS_TOTAL = 2

export type BoldFailureStep =
  | 'create_order'
  | 'create_order_partial'
  | 'bold_link'
  | 'bold_patch'
  | 'partial_links'

function extractErrorMessage(error: unknown, fallback: string): string {
  return extractAxiosApiErrorMessage(error, fallback)
}

const CreateOrder: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [responseData, setResponseData] = useState<any>(null)

  const [showBoldModal, setShowBoldModal] = useState(false)
  const [boldPhase, setBoldPhase] = useState<BoldModalPhase>('progress')
  const [boldPaymentUrl, setBoldPaymentUrl] = useState<string | null>(null)
  const [boldCreatedOrderId, setBoldCreatedOrderId] = useState<number | null>(null)
  const [boldProgress, setBoldProgress] = useState({
    current: 0,
    total: BOLD_PROGRESS_TOTAL,
    percentage: 0,
    currentPage: 0,
    totalPages: BOLD_PROGRESS_TOTAL
  })
  const [boldMessage, setBoldMessage] = useState('')
  const [boldPartialLinks, setBoldPartialLinks] = useState<BoldPartialLinkItem[]>([])
  const [boldPartialError, setBoldPartialError] = useState<string | null>(null)
  const [boldErrorMessage, setBoldErrorMessage] = useState<string | null>(null)
  const [boldPatchSavePending, setBoldPatchSavePending] = useState(false)
  const [isBoldRetrying, setIsBoldRetrying] = useState(false)
  const [isBoldCancelling, setIsBoldCancelling] = useState(false)
  const [productStockRefreshKey, setProductStockRefreshKey] = useState(0)

  const refreshProductStock = useCallback(() => {
    setProductStockRefreshKey((prev) => prev + 1)
  }, [])

  const boldPendingSubmitDataRef = useRef<Record<string, unknown> | null>(null)
  const boldPatchRetryContextRef = useRef<BoldPatchRetryContext | null>(null)
  const boldPartialPatchRetryRef = useRef<BoldPartialPatchContext | null>(null)

  let initialData = undefined
  const fromCotizacion = (location.state as { fromCotizacion?: unknown } | null)?.fromCotizacion
  if (fromCotizacion) {
    initialData = normalizeCotizacionToOrder(fromCotizacion)
  } else {
    const cotizacionData = localStorage.getItem('newOrderFromCotizacion')
    if (cotizacionData) {
      try {
        initialData = normalizeCotizacionToOrder(JSON.parse(cotizacionData))
      } catch {
        initialData = undefined
      }
    }
  }

  const resetBoldModalState = useCallback(() => {
    setShowBoldModal(false)
    setBoldPhase('progress')
    setBoldPaymentUrl(null)
    setBoldCreatedOrderId(null)
    setBoldPartialLinks([])
    setBoldPartialError(null)
    setBoldErrorMessage(null)
    setBoldPatchSavePending(false)
    boldPatchRetryContextRef.current = null
    boldPartialPatchRetryRef.current = null
  }, [])

  const openBoldError = useCallback(
    (params: {
      message: string
      step: BoldFailureStep
      orderId?: number | null
    }) => {
      setBoldErrorMessage(params.message)
      setBoldPatchSavePending(false)
      if (params.orderId != null) setBoldCreatedOrderId(params.orderId)
      setBoldPhase('error')
    },
    []
  )

  const navigateToCreatedOrder = useCallback(
    (orderId: number, toastMessage: string, icon: '✅' | '⚠️' = '✅') => {
      toast(toastMessage, { icon, position: 'top-center', duration: icon === '⚠️' ? 8000 : 4000 })
      localStorage.removeItem('newOrderFromCotizacion')
      navigate('/edit/' + orderId + '/')
    },
    [navigate]
  )

  const handleFlexyStockCreatedOrder = useCallback(
    (created: Parameters<typeof buildFlexyStockInsufficientToastMessage>[0]) => {
      navigateToCreatedOrder(
        created.orderId,
        buildFlexyStockInsufficientToastMessage(created, 'create'),
        '⚠️'
      )
    },
    [navigateToCreatedOrder]
  )

  const postCreateOrder = useCallback(async (data: Record<string, unknown>): Promise<number | null> => {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      toast.error('Error de autenticación', { icon: '⚠️', position: 'top-center' })
      return null
    }
    try {
      const response = await axiosInstance.post('/orders', data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return Number(response.data?.nuevoId ?? response.data?.orderId) || null
    } catch (error) {
      const created = parseFlexyStockInsufficientCreatedOrder(error)
      if (created) {
        throw new FlexyStockInsufficientOrderError(created)
      }
      throw error
    }
  }, [])

  const runPartialBoldLinkStep = useCallback(
    async (orderId: number, bump: (msg: string) => void) => {
      bump('Generando links Bold…')
      const genRes = await generateAndPatchBoldLinksForPartialRows({
        orderId,
        onProgress: (m) => bump(m)
      })
      setBoldPartialLinks(genRes.links)
      if (!genRes.ok && genRes.failureStep === 'bold_patch' && genRes.patchRetryContext) {
        boldPartialPatchRetryRef.current = genRes.patchRetryContext
        boldPatchRetryContextRef.current = null
        setBoldPatchSavePending(true)
        setBoldPartialError(genRes.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED)
        setBoldPhase('result')
        return false
      }
      setBoldPatchSavePending(false)
      boldPartialPatchRetryRef.current = null
      setBoldPartialError(genRes.ok ? null : genRes.error || null)
      if (!genRes.ok) {
        if (genRes.links.length === 0) {
          openBoldError({
            message: genRes.error || 'No se pudieron generar los enlaces de pago Bold',
            step: 'partial_links',
            orderId
          })
        } else {
          setBoldPhase('result')
          toast.error(genRes.error || 'No se generaron todos los enlaces de pago', {
            icon: '⚠️',
            position: 'top-center',
            duration: 6000
          })
        }
        return false
      }
      setBoldPhase('result')
      return true
    },
    [openBoldError]
  )

  const runSingleBoldLinkStep = useCallback(
    async (
      orderId: number,
      data: Record<string, unknown>,
      onProgress: (msg: string) => void,
      resumeStep: BoldFailureStep | null
    ) => {
      const totalAmount = Math.round(Number(data.total) || 0)
      const storeName = String(data.store_name || (data.extraDetails as Record<string, unknown> | undefined)?.store_name || '')
      const status = String(data.status || 'processing')
      const paymentMethod = String(data.payment_method || 'bold')

      if (resumeStep === 'bold_patch' && boldPatchRetryContextRef.current) {
        onProgress('Guardando transacción en el pedido…')
        const patchRes = await retrySaveBoldTransactionOnOrder(orderId, boldPatchRetryContextRef.current)
        if (!patchRes.ok) {
          setBoldPatchSavePending(true)
          setBoldPartialError(patchRes.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED)
          const url = boldPaymentUrl || boldPatchRetryContextRef.current.transactionId
          setBoldPaymentUrl(url)
          setBoldPhase('result')
          return false
        }
        const url = boldPaymentUrl || boldPatchRetryContextRef.current.transactionId
        setBoldPaymentUrl(url)
        setBoldPatchSavePending(false)
        setBoldPartialError(null)
        setBoldPhase('result')
        boldPatchRetryContextRef.current = null
        return true
      }

      const linkRes = await generateAndSaveSingleBoldLinkForOrder({
        orderId,
        totalAmount,
        storeName,
        status,
        paymentMethod,
        onProgress
      })

      if (!linkRes.ok) {
        if (linkRes.failureStep === 'bold_patch' && linkRes.patchRetryContext) {
          boldPatchRetryContextRef.current = linkRes.patchRetryContext
          boldPartialPatchRetryRef.current = null
          if (linkRes.url) setBoldPaymentUrl(linkRes.url)
          setBoldPatchSavePending(true)
          setBoldPartialError(linkRes.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED)
          setBoldPhase('result')
          return false
        }
        boldPatchRetryContextRef.current = null
        openBoldError({
          message: linkRes.error || 'No se pudo generar el enlace de pago Bold',
          step: linkRes.failureStep || 'bold_link',
          orderId
        })
        return false
      }

      setBoldPaymentUrl(linkRes.url || null)
      boldPatchRetryContextRef.current = null
      setBoldPatchSavePending(false)
      setBoldPartialError(null)
      setBoldPhase('result')
      return true
    },
    [boldPaymentUrl, openBoldError]
  )

  const runPartialBoldCreateFlow = useCallback(
    async (data: Record<string, unknown>, resumeFromOrderId?: number | null) => {
      setShowBoldModal(true)
      setBoldPhase('progress')
      setBoldPaymentUrl(null)
      setBoldPartialLinks([])
      setBoldPartialError(null)
      setBoldErrorMessage(null)
      setBoldPatchSavePending(false)

      const boldIdx = indicesPagosParcialesBoldSinTx(getPagosFromSubmitPayload(data))
      const stepsTotal = 2 + boldIdx.length * 2
      let step = 0
      const bump = (msg: string) => {
        step = Math.min(step + 1, stepsTotal)
        setBoldMessage(msg)
        setBoldProgress({
          current: step,
          total: stepsTotal,
          percentage: Math.min(100, Math.round((step / stepsTotal) * 100)),
          currentPage: step,
          totalPages: stepsTotal
        })
      }

      let orderId: number | null = resumeFromOrderId ?? null

      try {
        if (orderId == null) {
          setBoldMessage('Procesando pago Bold…')
          bump('Procesando pago Bold…')
          orderId = await postCreateOrder(data)
          if (orderId == null) {
            openBoldError({
              message: 'No se pudo completar la solicitud de pago Bold. Intenta de nuevo.',
              step: 'create_order_partial'
            })
            return
          }
          bump('Generando enlaces de pago…')
        } else {
          setBoldMessage('Generando enlaces de pago Bold…')
        }

        setBoldCreatedOrderId(orderId)
        await runPartialBoldLinkStep(orderId, bump)
      } catch (error) {
        const flexyCreated = getFlexyStockInsufficientFromError(error)
        if (flexyCreated) {
          resetBoldModalState()
          handleFlexyStockCreatedOrder(flexyCreated)
          return
        }
        if (isFlexyStockInsufficientError(error)) {
          resetBoldModalState()
          toast.error(extractErrorMessage(error, 'Stock insuficiente'), {
            icon: '⚠️',
            position: 'top-center',
            duration: 8000,
            style: { maxWidth: '560px', whiteSpace: 'pre-line' }
          })
          refreshProductStock()
          return
        }
        console.error('Error flujo parciales Bold:', error)
        openBoldError({
          message: extractErrorMessage(error, 'No se pudo generar el enlace de pago Bold'),
          step: orderId != null ? 'partial_links' : 'create_order_partial',
          orderId
        })
      }
    },
    [handleFlexyStockCreatedOrder, openBoldError, postCreateOrder, refreshProductStock, resetBoldModalState, runPartialBoldLinkStep]
  )

  const runSingleBoldCreateFlow = useCallback(
    async (
      data: Record<string, unknown>,
      resumeFromOrderId?: number | null,
      resumeStep?: BoldFailureStep | null
    ) => {
      setShowBoldModal(true)
      setBoldPhase('progress')
      setBoldPartialLinks([])
      setBoldPaymentUrl(null)
      setBoldErrorMessage(null)
      setBoldPatchSavePending(false)
      setBoldPartialError(null)

      const bumpProgress = (current: number, msg: string) => {
        setBoldMessage(msg)
        setBoldProgress({
          current,
          total: BOLD_PROGRESS_TOTAL,
          percentage: Math.round((current / BOLD_PROGRESS_TOTAL) * 100),
          currentPage: current,
          totalPages: BOLD_PROGRESS_TOTAL
        })
      }

      let orderId: number | null = resumeFromOrderId ?? null

      try {
        const skipCreate =
          orderId != null && (resumeStep === 'bold_link' || resumeStep === 'bold_patch')

        if (!skipCreate) {
          bumpProgress(1, 'Procesando pago Bold…')
          orderId = await postCreateOrder(data)
        }

        if (orderId == null) {
          openBoldError({
            message: 'No se pudo completar la solicitud de pago Bold. Intenta de nuevo.',
            step: 'create_order'
          })
          return
        }

        setBoldCreatedOrderId(orderId)
        bumpProgress(2, 'Generando link de pago Bold...')
        await runSingleBoldLinkStep(orderId, data, (msg) => bumpProgress(2, msg), resumeStep ?? null)
      } catch (error) {
        const flexyCreated = getFlexyStockInsufficientFromError(error)
        if (flexyCreated) {
          resetBoldModalState()
          handleFlexyStockCreatedOrder(flexyCreated)
          return
        }
        if (isFlexyStockInsufficientError(error)) {
          resetBoldModalState()
          toast.error(extractErrorMessage(error, 'Stock insuficiente'), {
            icon: '⚠️',
            position: 'top-center',
            duration: 8000,
            style: { maxWidth: '560px', whiteSpace: 'pre-line' }
          })
          refreshProductStock()
          return
        }
        console.error('Error al crear el pedido (Bold):', error)
        openBoldError({
          message: extractErrorMessage(error, 'No se pudo generar el enlace de pago Bold'),
          step: orderId != null ? 'bold_link' : 'create_order',
          orderId
        })
      }
    },
    [handleFlexyStockCreatedOrder, openBoldError, postCreateOrder, refreshProductStock, resetBoldModalState, runSingleBoldLinkStep]
  )

  const handleBoldModalAccept = () => {
    localStorage.removeItem('newOrderFromCotizacion')
    const id = boldCreatedOrderId
    resetBoldModalState()
    if (id != null) {
      window.location.href = '/edit/' + id + '/'
    }
  }

  const handleRetryPatchSave = async () => {
    const orderId = boldCreatedOrderId
    if (orderId == null) return

    setIsBoldRetrying(true)
    try {
      const singleCtx = boldPatchRetryContextRef.current
      const partialCtx = boldPartialPatchRetryRef.current
      if (singleCtx) {
        const res = await retrySaveBoldTransactionOnOrder(orderId, singleCtx)
        if (!res.ok) {
          setBoldPartialError(res.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED)
          return
        }
        boldPatchRetryContextRef.current = null
        setBoldPatchSavePending(false)
        setBoldPartialError(null)
        toast.success('Enlace guardado en el pedido', { icon: '✅', position: 'top-center' })
        return
      }
      if (partialCtx) {
        const res = await retrySaveBoldPartialLinksOnOrder(orderId, partialCtx)
        if (!res.ok) {
          setBoldPartialError(res.error || BOLD_LINK_SAVED_BUT_ORDER_NOT_UPDATED)
          return
        }
        boldPartialPatchRetryRef.current = null
        setBoldPatchSavePending(false)
        setBoldPartialError(null)
        toast.success('Enlaces guardados en el pedido', { icon: '✅', position: 'top-center' })
      }
    } finally {
      setIsBoldRetrying(false)
    }
  }

  /** Reintentar: elimina el pedido creado y cierra el modal (sin refrescar la página). */
  const handleBoldRetry = async () => {
    const orderId = boldCreatedOrderId

    if (orderId == null) {
      resetBoldModalState()
      return
    }

    setIsBoldRetrying(true)
    try {
      const result = await deleteOrderById(orderId)
      if (result.ok) {
        resetBoldModalState()
        return
      }
      toast.error(result.message || 'No se pudo completar la operación', {
        icon: '⚠️',
        position: 'top-center'
      })
    } finally {
      setIsBoldRetrying(false)
    }
  }

  /** Cerrar (X): limpia registro en servidor si aplica y refresca la página. */
  const handleBoldDismiss = async () => {
    const orderId = boldCreatedOrderId

    if (orderId == null) {
      resetBoldModalState()
      return
    }

    setIsBoldCancelling(true)
    try {
      const result = await deleteOrderById(orderId)
      if (result.ok) {
        localStorage.removeItem('newOrderFromCotizacion')
        resetBoldModalState()
        window.location.reload()
        return
      }
      toast.error(result.message || 'No se pudo completar la operación', {
        icon: '⚠️',
        position: 'top-center'
      })
    } finally {
      setIsBoldCancelling(false)
    }
  }

  const handleCopyBoldLink = async (url: string) => {
    const ok = await copyToClipboard(url)
    if (ok) toast.success('Link copiado al portapapeles', { icon: '📋', position: 'top-center' })
  }

  const handleSubmit = async (data: any) => {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      toast.error('Error de autenticación', { icon: '⚠️', position: 'top-center' })
      return
    }

    setResponseData(data)
    boldPendingSubmitDataRef.current = data as Record<string, unknown>

    const txValidation = await validateTransactionIdsForCreate(data)
    if (!txValidation.ok) {
      toast.error(txValidation.message, { icon: '⚠️', position: 'top-center' })
      return
    }

    const dataRecord = data as Record<string, unknown>
    const partialBoldFlow = submitPayloadNeedsPartialBoldLinks(dataRecord)

    if (partialBoldFlow) {
      await runPartialBoldCreateFlow(dataRecord)
      return
    }

    if (data.payment_method !== 'bold') {
      try {
        const response = await axiosInstance.post('/orders', data, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        toast.success(
          (t) => (
            <span>
              Pedido creado exitosamente <strong>#{response.data.nuevoId}</strong>
            </span>
          ),
          { icon: '✅', position: 'top-center' }
        )
        localStorage.removeItem('newOrderFromCotizacion')
        navigate('/edit/' + response.data.nuevoId + '/')
      } catch (error) {
        const flexyCreated = getFlexyStockInsufficientFromError(error)
        if (flexyCreated) {
          handleFlexyStockCreatedOrder(flexyCreated)
          return
        }
        console.error('Error al crear el pedido:', error)
        toast.error(extractErrorMessage(error, 'Error al crear el pedido'), {
          icon: '⚠️',
          position: 'top-center',
          duration: isFlexyStockInsufficientError(error) ? 8000 : 4000,
          style: { maxWidth: '560px', whiteSpace: 'pre-line' }
        })
        if (isFlexyStockInsufficientError(error)) {
          refreshProductStock()
        }
      }
      return
    }

    boldPatchRetryContextRef.current = null
    await runSingleBoldCreateFlow(dataRecord)
  }

  return (
    <div className="px-12 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/orders')}
            className="text-blue-800 hover:text-blue-900 transition-colors"
            type="button"
            aria-label="Volver a pedidos"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-semibold text-blue-800">
            Crear pedido
          </h1>
        </div>
      </div>

      <OrderForm
        mode="create"
        onSubmit={handleSubmit}
        tiposDeDocumento={tiposDeDocumento}
        departamentos={Departamentos}
        ciudades={Ciudades}
        formaPago={formaPago}
        metodoPago={metodoPago}
        plazosPago={plazosPago}
        initialData={initialData}
        productStockRefreshKey={productStockRefreshKey}
      />

      {responseData && <div className="mt-8 p-4 bg-white rounded-lg shadow" />}

      <BoldCreateOrderModal
        isOpen={showBoldModal}
        phase={boldPhase}
        progress={boldProgress}
        message={boldMessage}
        paymentUrl={boldPaymentUrl}
        partialLinks={boldPartialLinks}
        partialError={boldPartialError}
        errorMessage={boldErrorMessage}
        isRetrying={isBoldRetrying}
        isCancelling={isBoldCancelling}
        patchSavePending={boldPatchSavePending}
        onAccept={handleBoldModalAccept}
        onRetry={handleBoldRetry}
        onRetryPatchSave={handleRetryPatchSave}
        onDismiss={handleBoldDismiss}
        onCopyLink={handleCopyBoldLink}
      />
    </div>
  )
}

export default CreateOrder
